const APPLE_JS_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

type AppleName = {
  firstName?: string | null;
  lastName?: string | null;
};

type AppleUser =
  | {
      name?: AppleName | null;
      email?: string | null;
    }
  | string
  | null
  | undefined;

type AppleSignInResponse = {
  authorization?: {
    id_token?: string;
    state?: string;
  };
  user?: AppleUser;
};

type AppleAuth = {
  init(config: {
    clientId: string;
    scope: string;
    redirectURI: string;
    state: string;
    nonce: string;
    usePopup: boolean;
  }): void;
  signIn(): Promise<AppleSignInResponse>;
};

declare global {
  interface Window {
    AppleID?: {
      auth?: AppleAuth;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function randomToken(): string {
  const bytes = new Uint8Array(32);
  globalThis.crypto?.getRandomValues?.(bytes);
  if (bytes.every((byte) => byte === 0)) {
    throw new Error("Secure browser crypto is unavailable.");
  }
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function loadAppleScript(): Promise<void> {
  if (typeof document === "undefined") {
    return Promise.reject(new Error("Apple web sign-in needs a browser."));
  }

  if (window.AppleID?.auth) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${APPLE_JS_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => {
          scriptPromise = null;
          reject(new Error("Apple sign-in script failed to load."));
        },
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.src = APPLE_JS_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("Apple sign-in script failed to load."));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

function parseAppleUser(user: AppleUser): { fullName?: string } {
  // The display name is OPTIONAL and Apple only ever sends it on the first
  // authorization, so a malformed payload must not fail a sign-in that already
  // holds a valid identity token. This `JSON.parse` used to be unguarded: a
  // SyntaxError from it propagated out of `signInWithAppleWeb` and was
  // indistinguishable, at the call site, from a real auth failure.
  let parsed: Exclude<AppleUser, string> = null;
  if (typeof user === "string" && user.trim()) {
    try {
      parsed = JSON.parse(user) as Exclude<AppleUser, string>;
    } catch {
      parsed = null;
    }
  } else if (typeof user !== "string") {
    parsed = user;
  }

  const fullName = [
    parsed && typeof parsed === "object" ? parsed.name?.firstName : undefined,
    parsed && typeof parsed === "object" ? parsed.name?.lastName : undefined,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return { fullName: fullName || undefined };
}

/**
 * Did the person close the popup, rather than hit a failure?
 *
 * Apple's web SDK does not reject with an `Error`. It rejects with a bare
 * object carrying an `error` string, which is why an `instanceof Error` check
 * at the call site treats a cancellation as a failure and tells someone who
 * just changed their mind to try again. The iOS SDK has an equivalent
 * (`ERR_REQUEST_CANCELED`) and `welcome.tsx` has always suppressed that one.
 *
 * `user_cancelled_authorize` is the value Apple documents.
 * `popup_closed_by_user` is defensive: it is widely reported in the wild but
 * is NOT in Apple's documentation, so treat it as an unverified extra rather
 * than a second known case. Matching a value Apple never sends costs nothing;
 * missing one it does send means a spurious "try again".
 *
 * Both, if they arrive, mean the same thing to us: say nothing.
 */
export function isAppleWebCancel(error: unknown): boolean {
  const code = (error as { error?: unknown } | null)?.error;

  return (
    code === "popup_closed_by_user" || code === "user_cancelled_authorize"
  );
}

export async function signInWithAppleWeb(config: {
  clientId: string;
  redirectUri: string;
}): Promise<{
  identityToken: string;
  fullName?: string;
  nonce: string;
}> {
  if (!config.clientId || !config.redirectUri) {
    throw new Error("Apple web sign-in is not configured.");
  }

  await loadAppleScript();
  const auth = window.AppleID?.auth;
  if (!auth) throw new Error("Apple sign-in script did not initialize.");

  const state = randomToken();
  const nonce = randomToken();
  auth.init({
    clientId: config.clientId,
    scope: "name email",
    redirectURI: config.redirectUri,
    state,
    nonce,
    usePopup: true,
  });

  const response = await auth.signIn();
  const identityToken = response.authorization?.id_token;
  if (response.authorization?.state !== state) {
    throw new Error("Apple sign-in state mismatch.");
  }
  if (!identityToken) {
    throw new Error("Apple sign-in returned no identity token.");
  }

  return {
    identityToken,
    nonce,
    ...parseAppleUser(response.user),
  };
}
